import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, type ActivityEntry, type SessionRecordingView } from '../../api/client';
import { pageName, replayUrl } from '../../components/SessionReplay';
import { useTenant } from '../../components/Shell';
import { Button, Card, Dialog, Empty, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { dateOnly, fullWhen, plural, shortWhen } from '../../lib/format';
import { timesIn } from '../../lib/time';
import { useEarlier } from '../../lib/useEarlier';
import { useData, type DataState } from '../../lib/useData';
import { Icon } from '../../lib/icons';
import { StorageChip } from './RecordingSettings';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { RULE_WORDS } from '../../lib/textRouting';

const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
const q = (v: unknown) => (str(v) ? `“${str(v)}”` : '');
const ext = (d: Record<string, unknown>) => (str(d.extension) ? ` (extension ${str(d.extension)})` : '');
/** A list of extensions from an entry's detail. */
const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);
/** A phone number from an entry's detail, as people write it. */
const tel = (v: unknown) => (str(v) ? formatPhoneNumber(str(v)) : 'a number');
/** A saved copy's subject (2718): "ring group “Front Office” (300)". */
const COPY_KINDS: Record<string, string> = { ringGroup: 'ring group', callQueue: 'call queue', autoAttendant: 'auto attendant' };
const copyName = (d: Record<string, unknown>) => `${COPY_KINDS[str(d.kind)] ?? 'a ring group or queue'}${str(d.name) ? ` ${q(d.name)}` : ''}${str(d.extension) ? ` (${str(d.extension)})` : ''}`;

/** Billing (Stitch Admin → Billing): an amount in cents as dollars ("$1,234.56", "−$8.00"). */
const dollars = (v: unknown): string => {
  const c = Number(v);
  if (!Number.isFinite(c)) return 'an amount';
  return `${c < 0 ? '−' : ''}$${(Math.abs(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
/** A billing day ("2026-12-01") or month as written: "Dec 1, 2026", "December 2026" (billing days are Eastern calendar days). */
const billingDay = (v: unknown): string => (/^\d{4}-\d{2}-\d{2}/.test(str(v)) ? new Date(`${str(v).slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : 'a day');
const billingMonth = (v: unknown): string => (/^\d{4}-\d{2}/.test(str(v)) ? new Date(`${str(v).slice(0, 7)}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : 'the month');
/** A discount or fee's size: "10%" or "$5.00". */
const howMuch = (method: unknown, value: unknown) => (str(method) === 'percent' ? `${Number(value) || 0}%` : dollars(value));
/** What a fee is charged on. */
const FEE_BASIS: Record<string, string> = {
  voice_line: 'per voice line',
  number: 'per number',
  invoice: 'per invoice',
  voice_charges: 'of voice charges',
  recurring: 'of plans and numbers',
  subtotal: 'of the subtotal'
};
/** Usage meters (stitch-rating), by their code. */
const USAGE_METERS: Record<string, string> = {
  calling_minutes: 'calling minutes',
  tollfree_minutes: 'toll-free minutes',
  texts: 'texts',
  bulk_texts: 'bulk texts',
  live_minutes: 'Stitch Live minutes',
  ai_actions: 'AI actions',
  meeting_recording_minutes: 'meeting recording minutes',
  numbers_held: 'numbers held',
  storage_gb: 'storage'
};
/** A unit price in cents, which can have fractions: "2¢", "1.5¢", "$0.44". */
const unitCents = (v: unknown): string => {
  const c = Number(v);
  if (!Number.isFinite(c)) return 'a price';
  return c >= 100 ? dollars(c) : `${Number(c.toFixed(4))}¢`;
};
/** What a billing account's change touched. */
const ACCOUNT_FIELDS: Record<string, string> = { startedOn: 'start day', billsFrom: 'first billing day', status: 'status', defaultPlan: 'plan for new people', net30: 'invoice terms', billTo: 'billing address' };
/** Who or what a billing event is about: a person, a shared phone, a number, the company. */
function billingWho(d: Record<string, unknown>): string {
  const key = str(d.key);
  if (key.startsWith('number:')) return tel(key.slice(7));
  if (str(d.label)) return str(d.label);
  if (key.startsWith('shared:')) return `shared phone ${key.slice(7)}`;
  if (key.startsWith('user:')) return 'a person';
  return 'the company';
}

/** Where a number's texts go, for Activity: "call queue 500", "where its calls go". */
function textsWhere(kind: unknown, target: unknown): string {
  switch (str(kind)) {
    case 'person':
      return `extension ${str(target)}`;
    case 'ringGroup':
      return `ring group ${str(target)}`;
    case 'callQueue':
      return `call queue ${str(target)}`;
    case 'none':
      return 'nobody (texting off)';
    default:
      return 'where its calls go';
  }
}

/** Phone numbers from an entry's detail: the first few, then how many more. */
function tels(v: unknown, most = 3): string {
  const all = list(v).map(tel);
  return all.length > most ? `${all.slice(0, most).join(', ')} and ${all.length - most} more` : all.join(', ');
}

/** A time from an entry's detail, as people read it ("" when there isn't one). */
const when = (v: unknown) => (str(v) && !Number.isNaN(Date.parse(str(v))) ? fullWhen(str(v)) : '');

/** Stitch's updates for a company (1.12.33), in plain words: its name, then what applying and putting back did (and the "tried to" form). */
const UPDATES: Record<string, { name: string; apply: [string, string]; putBack: [string, string] }> = {
  'app-calling': { name: 'App calling', apply: ['turned on App calling', 'turn on App calling'], putBack: ['turned off App calling', 'turn off App calling'] },
  'voicemail-emails': {
    name: 'Stitch’s voicemail emails',
    apply: ['switched the company to Stitch’s voicemail emails', 'switch the company to Stitch’s voicemail emails'],
    putBack: ['put back the phone system’s own voicemail emails', 'put back the phone system’s own voicemail emails']
  },
  'mailbox-policy': {
    name: 'Make room for new messages on every mailbox',
    apply: ['set every mailbox to Make room for new messages', 'set every mailbox to Make room for new messages'],
    putBack: ['put back what each mailbox had for old messages', 'put back what each mailbox had for old messages']
  },
  'patch-rings': { name: 'Patch rings', apply: ['made Patch rings for everyone', 'make Patch rings for everyone'], putBack: ['took the Patch rings off', 'take the Patch rings off'] }
};
const updateOf = (v: unknown) => UPDATES[str(v)] ?? { name: 'an update', apply: ['ran an update', 'run an update'], putBack: ['put back an update', 'put back an update'] };

/** What a company's details at Stitch are called (customer.changed). */
const CUSTOMER_FIELDS: Record<string, string> = { name: 'name', contactName: 'contact name', contactEmail: 'contact email', notes: 'notes', domain: 'phone system link', status: 'status' };

/** Where a number order stands after Stitch tried it again. */
const ORDER_NOW: Record<string, string> = {
  working: 'still working on it',
  done: 'all done',
  partial: 'some of the numbers are ready',
  failed: 'it didn’t work',
  needs_staff: 'Stitch support is finishing it',
  porting: 'still moving',
  cancelled: 'cancelled'
};

/** Why live help ended. */
/** Retention (Storage A2, 2717): what each kind is called, and days in words. */
const RETENTION_KIND: Record<string, string> = {
  calls: 'call records',
  recordings: 'call recordings',
  voicemail: 'voicemail',
  messages: 'messages and texts',
  transcripts: 'transcripts',
  patches: 'Patches'
};
const keptFor = (v: unknown): string => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 'a while';
  if (n === 182) return '6 months';
  if (n === 730) return '24 months';
  if (n >= 365 && n % 365 === 0) return n === 365 ? '1 year' : `${n / 365} years`;
  return `${n} days`;
};

const LIVE_ENDED: Record<string, string> = {
  customer: 'they ended it',
  staff: 'Stitch support ended it',
  'staff-left': 'Stitch support closed their viewer',
  'no-answer': 'nobody answered',
  declined: 'they said no',
  lost: 'the connection was lost',
  time: 'it reached its time limit',
  busy: 'Stitch support was busy with other people'
};

/** An action with no words of its own yet, still readable: "Changed something (dnd turned off)". */
export function activityFallback(action: string): string {
  return `Changed something (${action.replace(/[._]+/g, ' ').trim()})`;
}

/** What someone did, in plain words. */
export function describeActivity(e: Pick<ActivityEntry, 'action' | 'detail'>): string {
  const d = e.detail;
  switch (e.action) {
    case 'signed_in':
      return 'Signed in';
    case 'password.changed':
      return 'Changed their password';
    case 'password.reset':
      return 'Reset their password with an emailed link';
    case 'password.reset_requested':
      return 'Asked for a password reset link';
    case 'password.reset_link':
      return d.emailed ? `Emailed ${str(d.user) || 'someone'} a password reset link` : `Made a password reset link for ${str(d.user) || 'someone'} to pass on`;
    case 'invite.sent':
      if (d.messagingOnly) return `Invited ${str(d.email) || 'someone'} to the portal as messaging only (no phone)`;
      return `Invited ${str(d.email) || 'someone'} to the portal${str(d.role) ? ` as ${str(d.role) === 'admin' ? 'an admin' : 'a user'}` : ''}`;
    case 'invite.accepted':
      return 'Accepted their invitation';
    case 'invite.revoked':
      return `Cancelled the invitation for ${str(d.email) || 'someone'}`;
    case 'access.changed':
      if (d.messagingOnly) return `Changed portal access for ${str(d.user) || 'someone'}: messaging only (no phone)`;
      return `Changed portal access for ${str(d.user) || 'someone'}${str(d.customRole) ? `: ${str(d.customRole)}` : str(d.role) ? `: ${str(d.role) === 'admin' ? 'admin' : 'user'}` : ''}`;
    case 'customer.messaging_seats':
      return `Set how many messaging-only people the plan includes: ${str(d.to) === 'default' ? 'Stitch’s default' : str(d.to)}`;
    case 'seats.requested':
      return `Asked Stitch for ${plural(Number(d.more) || 0, 'more messaging-only person', 'more messaging-only people')}`;
    case 'seats.request_done':
      return `Answered the request for ${plural(Number(d.more) || 0, 'more messaging-only person', 'more messaging-only people')}: the plan includes ${str(d.to)} now`;
    case 'seats.request_declined':
      return `Declined the request for ${plural(Number(d.more) || 0, 'more messaging-only person', 'more messaging-only people')}${str(d.reason) ? `: ${q(d.reason)}` : ''}`;
    case 'features.requested':
      return `Asked Stitch to turn on ${str(d.featureName) || str(d.feature) || 'a feature'}`;
    case 'features.request_done':
      return `Turned on ${str(d.featureName) || str(d.feature) || 'a feature'}, as the company asked`;
    case 'features.request_closed':
      return `Answered the request for ${str(d.featureName) || str(d.feature) || 'a feature'}${str(d.reason) ? `: ${q(d.reason)}` : ''}`;
    case 'access.removed':
      return `Removed portal access for ${str(d.user) || 'someone'}`;
    case 'role.created':
      return `Created the role ${q(d.name)}`;
    case 'role.changed':
      return `Changed the role ${q(d.name)}`;
    case 'role.removed':
      return `Deleted the role ${q(d.name)}`;
    case 'company.transcripts':
      return d.on ? 'Switched voicemail transcripts on for the company' : 'Switched voicemail transcripts off for the company (and deleted them)';
    case 'company.transcripts_every':
      return d.on ? 'Wrote out voicemail for every mailbox, new ones too (except any switched off)' : 'Wrote out voicemail only for the mailboxes chosen (and deleted the others’ transcripts)';
    case 'company.transcripts_self':
      return d.on ? 'Let people turn voicemail transcripts on or off for their own voicemail' : 'Stopped people turning their own voicemail transcripts on or off';
    case 'voicemail.transcripts': {
      const boxes = Array.isArray(d.mailboxes) ? d.mailboxes.map(str).filter(Boolean) : [];
      const count = typeof d.count === 'number' ? d.count : boxes.length;
      const which = count === 1 && boxes[0] ? `extension ${boxes[0]}` : `${count} mailboxes`;
      return d.on ? `Turned on voicemail transcripts for ${which}` : `Turned off voicemail transcripts for ${which} (and deleted them)`;
    }
    case 'company.time_zone':
      return `Set the company time zone to ${str(d.to) || 'Stitch’s standard'}`;
    case 'template.created':
      return `Created the phone template ${q(d.template)}`;
    case 'template.removed':
      return `Deleted the phone template ${q(d.template)}`;
    case 'call.opened':
      return 'Opened a call’s details';
    case 'call.recording_played':
    case 'recording.played':
      return `Played a call recording${ext(d)}`;
    case 'voicemail.played':
      return `Listened to a voicemail${ext(d)}`;
    case 'voicemail.read':
      return `Read voicemail transcripts${ext(d)}`;
    case 'voicemail.noted':
      return `Added a note to a voicemail${ext(d)}`;
    case 'voicemail.moved':
      return `Moved a voicemail to ${str(d.to) || 'another folder'}${ext(d)}`;
    case 'voicemail.deleted':
      return `Deleted a voicemail${ext(d)}`;
    case 'support.requested':
      return 'Asked Stitch support for help';
    case 'support.closed':
      return 'Closed a support request';
    case 'support.reopened':
      return 'Reopened a support request';
    case 'live.asked':
      return 'Asked for live help';
    case 'session.recording_started':
      return 'Started a recorded session';
    case 'session.replayed':
      return `Watched ${str(d.person) || 'someone'}’s session recording`;
    case 'session.recording_deleted':
      return `Deleted ${str(d.person) || 'someone'}’s session recording`;
    case 'recording.settings':
      return d.enabled ? `Changed session recording: on, kept ${str(d.retentionDays)} days${str(d.ownStorage) ? `, copied to ${str(d.ownStorage)}` : ''}` : 'Switched session recording off';
    case 'change.applied':
      return `Changed: ${str(d.title)}`;
    case 'change.planned':
      return `Planned, not applied yet: ${str(d.title)}`;
    case 'change.undone':
      return `Changed, then undid it: ${str(d.title)}`;
    case 'change.rolled_back':
      return `Tried, and it was put back: ${str(d.title)}`;
    case 'change.needs_attention':
      return `Tried, needs Stitch support: ${str(d.title)}`;
    case 'change.discarded':
      return `Planned, then discarded: ${str(d.title)}`;
    case 'change.stale':
      return `Planned, out of date: ${str(d.title)}`;
    // Smart messaging (docs/smart-messaging.md): counts and scope only, never what anyone wrote.
    case 'messages.retention_deleted':
      return `Deleted old messages by your keep rules: ${plural(Number(d.chatMessages) || 0, 'chat message')} and ${plural(Number(d.texts) || 0, 'text')}`;
    case 'messages.files_removed':
      return `Removed old files in Messages by your file retention (${Number(d.days) || 90} days): ${plural(Number(d.files) || 0, 'file')}`;
    case 'messages.smart_settings': {
      // File retention (2936) is named, since it removes files.
      const f = d.fileDays as { from?: unknown; to?: unknown } | undefined;
      return f && typeof f === 'object' ? `Changed the Messages settings: files kept ${Number(f.to) || 90} days (was ${Number(f.from) || 90})` : 'Changed the Messages settings';
    }
    case 'messages.not_spam':
      return 'Marked a text conversation not spam';
    case 'messages.catch_up':
      return `Asked for a catch-up on ${d.thread ? 'a thread, ' : ''}${plural(Number(d.messages) || 0, 'message')} (AI)`;
    // Shared texting (1.12.32): Stitch AI's summary beside a team's conversation. How many it read, never what.
    case 'messages.text_summary':
      return `A summary was made for a shared text conversation (${plural(Number(d.messages) || 0, 'message')})`;
    case 'messages.export':
      return `Exported ${plural(Number(d.messages) || 0, 'message')}, ${str(d.from)} to ${str(d.to)}`;
    // Channels (1.12.19): a private channel is never named here (admins who aren't in it read this).
    case 'messages.channel_created':
      return d.private ? 'Made a private channel' : `Made the channel #${str(d.channel)}${str(d.access) === 'everyone' ? ' for everyone' : ''}`;
    case 'messages.channel_renamed':
      // Made Private in the same change: only the name it had before.
      return d.private ? (d.from ? `Renamed #${str(d.from)}, now a private channel` : 'Renamed a private channel') : `Renamed #${str(d.from)} to #${str(d.to)}`;
    case 'messages.channel_topic':
      return d.private ? 'Changed a private channel’s topic' : `Changed the topic of #${str(d.channel)}`;
    case 'messages.channel_archived':
      return d.private ? 'Archived a private channel' : `Archived #${str(d.channel)}`;
    case 'messages.channel_unarchived':
      return d.private ? 'Unarchived a private channel' : `Unarchived #${str(d.channel)}`;
    case 'messages.channel_access': {
      const kind = (v: unknown) => (str(v) === 'everyone' ? 'for everyone' : str(v) === 'private' ? 'private' : 'open');
      return `Made #${str(d.channel)} ${kind(d.to)} (it was ${kind(d.from)})`;
    }
    case 'messages.channel_people_added':
      return d.private ? `Added ${plural(Number(d.count) || list(d.people).length, 'person', 'people')} to a private channel` : `Added ${plural(list(d.people).length, 'person', 'people')} to #${str(d.channel)}`;
    case 'messages.channel_person_removed':
      return d.private ? 'Took someone out of a private channel' : `Took someone out of #${str(d.channel)}`;
    case 'messages.webhook_created':
    case 'messages.webhook_replaced':
    case 'messages.webhook_deleted':
    case 'messages.webhook_paused':
    case 'messages.webhook_resumed':
    case 'messages.webhook_renamed': {
      const verb = { created: 'Added', replaced: 'Replaced the address of', deleted: 'Deleted', paused: 'Paused', resumed: 'Resumed', renamed: 'Renamed' }[e.action.slice('messages.webhook_'.length)] ?? 'Changed';
      const name = str(d.name) || str(d.to);
      return `${verb} the webhook ${name ? `${q(name)} ` : ''}${d.private ? 'in a private channel' : `in #${str(d.channel)}`}`;
    }
    // Texts (1.12.30): numbers and names only, never what a text said.
    case 'texts.started':
      return `Started texting ${tel(d.to)} from ${tel(d.from)}`;
    case 'texts.contact_named':
      return `Named ${tel(d.number)} ${q(d.name)}${str(d.before) ? ` (it was ${q(d.before)})` : ''}`;
    case 'texts.contact_name_cleared':
      return str(d.before) ? `Took the name ${q(d.before)} off ${tel(d.number)}` : `Took the name off ${tel(d.number)}`;
    case 'texts.deleted':
      return `Deleted a text in the conversation with ${tel(d.number)}`;
    case 'texts.restored':
      return `Brought back a deleted text in the conversation with ${tel(d.number)}`;
    case 'texts.delete_setting':
      return d.on ? 'Let people delete texts' : 'Stopped people deleting texts';
    case 'messages.undo_setting':
      return d.on ? 'Let people bring a deleted message back for a day' : 'Made deleting a message final at once (no undo)';
    // Shared texting (1.12.32): where texts go, teams' rules, who has a conversation, STOP and HELP.
    case 'texts.route_set': {
      const before = d.before && typeof d.before === 'object' ? (d.before as Record<string, unknown>) : null;
      const was = before ? textsWhere(before.kind, before.target) : 'where its calls go';
      if (str(d.kind) === 'none') return `Turned texting off for ${tel(d.number)} (before: ${was})`;
      return `Changed where texts to ${tel(d.number)} go: ${textsWhere(d.kind, d.target)} (before: ${was})`;
    }
    case 'texts.team_rules': {
      const changed = list(d.changed).map((k) => RULE_WORDS[k as keyof typeof RULE_WORDS] ?? k.replace(/[A-Z]/g, (c) => ` ${c.toLowerCase()}`));
      const team = str(d.name) || (str(d.team) ? `team ${str(d.team)}` : 'a team');
      return changed.length ? `Changed ${team}’s texting rules: ${changed.join(', ')}` : `Saved ${team}’s texting rules, with no changes`;
    }
    case 'texts.help_text':
      return d.custom ? 'Changed the answer to HELP texts' : 'Put back Stitch’s answer to HELP texts';
    // Shared phones (1.13.0, 3067).
    case 'people.shared_phone_set':
      return d.new ? `Made extension ${str(d.extension)} a shared phone: ${str(d.name)}` : `Changed shared phone ${str(d.name)} (extension ${str(d.extension)})`;
    case 'people.shared_phone_removed':
      return `Made extension ${str(d.extension)} a person’s phone again, not a shared phone`;
    // Texting registration and moves to a team (1.13.0, 3073).
    case 'texts.moved':
      return `Moved the conversation with ${tel(d.contact)} from ${tel(d.from)} to ${str(d.name) || 'a team'} (${tel(d.to)})${d.teamText === 'quiet' ? '; the team’s text waits for morning where they are' : ''}`;
    case 'texts.move_texts':
      return 'Changed the texts sent when a conversation moves to a team';
    case 'texting.registration_submitted':
      return d.forced ? 'Stitch staff submitted the texting registration to the carriers' : 'Submitted the texting registration to the carriers';
    case 'texting.registration_resubmitted':
      return d.useFix ? 'Resubmitted the texting registration with Leland’s fix' : 'Resubmitted the texting registration';
    case 'texting.registration_held':
      return `Stitch staff held the texting registration${str(d.reason) ? `: ${str(d.reason)}` : ''}`;
    case 'texting.registration_released':
      return 'Stitch staff released the texting registration';
    case 'texting.registration_retired':
      return 'Stitch staff retired the texting campaign';
    case 'texting.registration_imported':
      return 'Stitch staff linked a texting registration made at the carrier';
    case 'texting.number_allowed':
      return `Stitch staff let ${tel(d.number)} text anyway${str(d.reason) ? `: ${str(d.reason)}` : ''}`;
    case 'texting.number_allow_removed':
      return `Stitch staff stopped letting ${tel(d.number)} text anyway`;
    case 'texting.number_p2p':
      // 1.13.0: admins turn it on (agreeing to the carrier's rules); staff turn it on, or mark one done in the carrier's portal.
      return d.promise
        ? `Turned on person-to-person texting (P2P) for ${tel(d.number)}, agreeing to its rules`
        : d.markOnly
          ? `Stitch staff marked ${tel(d.number)} as person-to-person texting (P2P), turned on in the carrier’s portal`
          : `Stitch staff turned on person-to-person texting (P2P) for ${tel(d.number)} at the carrier`;
    case 'archive.settings':
      return 'Changed the archive bucket settings (Stitch Admin → System → Archive)';
    case 'archive.key_from_block_storage':
      return `Set the archive to use Stitch Block Storage’s key (${str(d.bucket) || 'its bucket'}${str(d.folder) ? ` / ${str(d.folder)}` : ''})`;
    case 'retention.default_set':
      return `Set how long Stitch keeps records by default to ${keptFor(d.days)}, from ${keptFor(d.before)} (Stitch Admin → System → Archive)`;
    case 'retention.deletions_switched':
      return d.on
        ? 'Turned on deleting what companies confirmed past their retention (Stitch Admin → System → Retention)'
        : 'Turned off deleting past retention: counts only (Stitch Admin → System → Retention)';
    case 'retention.hot_data_switched':
      return d.on
        ? `Turned on keeping ${Number(d.days) || 90} days of calls in Stitch’s database, older ones read from the archive (Stitch Admin → System → Retention)`
        : 'Turned off trimming Stitch’s database: calls stay there until the records service’s own limit (Stitch Admin → System → Retention)';
    case 'archive.backfill':
      return `Queued ${Number(d.queued ?? 0).toLocaleString('en-US')} calls Stitch already kept for the archive`;
    // Restore drills (1.13.0, 3123): Stitch staff read a day of the company's archive back and checked it. Read only.
    case 'archive.drill_requested':
      return `Stitch staff asked for a restore drill of ${billingDay(d.day)}: reading that day back from the archive and checking it (nothing is changed)`;
    case 'archive.drill_finished':
      return d.passed
        ? `Restore drill of ${billingDay(d.day)} passed: ${Number(d.files ?? 0).toLocaleString('en-US')} files read back from ${list(d.copies).includes('company') ? 'Stitch’s copy and your own storage' : 'Stitch’s copy'}, every checksum and count matching`
        : `Restore drill of ${billingDay(d.day)}: ${Number(d.problems ?? 0).toLocaleString('en-US')} ${Number(d.problems) === 1 ? 'thing' : 'things'} didn’t match reading the archive back (nothing was changed)`;
    // Settings → Storage and retention (Storage A1): the company's own copy.
    case 'storage.connected':
      return `Connected your own storage (${str(d.bucket)}${str(d.folder) ? ` / ${str(d.folder)}` : ''})${d.mode === 'sealed' ? `, sealed with archive key ${str(d.keyId)}` : ''}${d.adopted ? ', picking up the archive already there' : ''}${d.backfill ? ', copying what Stitch keeps' : ''}`;
    case 'storage.deletes':
      return d.deletesBy === 'stitch' ? 'Let Stitch keep the retention in your own storage too' : 'Let your own storage’s rules decide what’s deleted there';
    case 'storage.backfill':
      return `Copied everything Stitch keeps to your own storage again (${Number(d.files ?? 0).toLocaleString('en-US')} files)`;
    case 'storage.removed':
      return d.mode === 'sealed'
        ? `Removed your own storage (${str(d.bucket)}): Stitch forgot its key and the archive key; the sealed files stay there, and only your key bundle opens them`
        : `Removed your own storage (${str(d.bucket)}): Stitch forgot its key; the files stay there`;
    case 'storage.seal_key_made':
      return `Made a key bundle for a sealed copy of your history (key ${str(d.keyId)}, fingerprint ${str(d.fingerprint)})`;
    case 'storage.behind':
      return `Stitch couldn’t reach your own storage (${str(d.bucket)}) for a while, and emailed your admins`;
    case 'storage.retention_set':
      return `Set how long Stitch keeps ${RETENTION_KIND[str(d.kind)] ?? 'records'} to ${keptFor(d.days)}${d.default ? ' (Stitch’s default)' : ''}, from ${keptFor(d.before)}`;
    case 'storage.retention_confirmed':
      return `Confirmed that Stitch may delete ${Number(d.items ?? 0).toLocaleString('en-US')} ${RETENTION_KIND[str(d.kind)] ?? 'records'} from before ${billingDay(d.cutoff)} (kept ${keptFor(d.days)})`;
    case 'storage.retention_deleted':
      // Stitch itself (stitch-archive) carrying out what the admin confirmed: Stitch's copy only.
      return `Stitch deleted ${Number(d.items ?? 0).toLocaleString('en-US')} ${RETENTION_KIND[str(d.kind)] ?? 'records'} from before ${billingDay(d.cutoff)} from its copy (kept ${keptFor(d.days)})`;
    case 'storage.export_requested':
      return 'Asked for a download of everything in the archive';
    case 'storage.export_downloaded':
      return `Downloaded everything in the archive (${Number(d.files ?? 0).toLocaleString('en-US')} files)`;
    case 'storage.message_search':
      return d.on ? 'Let your company’s admins search every archived message in History' : 'Stopped your company’s admins searching every archived message (each person sees the conversations they’re in)';
    // History's playback and downloads from the archive (2716).
    case 'history.played':
    case 'history.downloaded': {
      const what = ({ calls: 'an archived call recording', voicemails: 'an archived voicemail', messages: 'a file from an archived message', meetings: 'an archived meeting recording' } as Record<string, string>)[str(d.table)] ?? 'something from the archive';
      return `${e.action === 'history.played' ? (d.table === 'messages' ? 'Opened' : 'Played') : 'Downloaded'} ${what}${d.kept === 'your-storage' ? ', from your own storage' : ''}`;
    }
    case 'history.meeting_attendance':
      return `Downloaded an archived meeting’s attendance report${d.kept === 'your-storage' ? ', from your own storage' : ''}`;
    case 'history.meeting_chat':
      return `Downloaded an archived meeting’s chat and events${d.kept === 'your-storage' ? ', from your own storage' : ''}`;
    case 'field_test.recorded':
      return `Recorded the field test “${str(d.test)}” as ${({ passed: 'passed', failed: 'failed', blocked: 'couldn’t try', skipped: 'skipped', not_run: 'not done' } as Record<string, string>)[str(d.status)] ?? str(d.status)}`;
    // The gateway's field test tools, run from Stitch Admin (3131).
    case 'field_test.tools':
      return d.on ? `Stitch staff turned on the gateway’s field test tools here for ${Number(d.minutes ?? 120)} minutes` : 'Stitch staff turned off the gateway’s field test tools';
    case 'field_test.watch':
      return `Stitch staff watched extension ${str(d.ext)}’s calls from ${str(d.from)}’s line, for a field test`;
    case 'field_test.pickup':
      return `Stitch staff picked up a watched call (d${str(d.dialog)}) on ${str(d.from)}’s line, for a field test${d.swap ? ' (the tags the other way round)' : ''}${d.toOther ? ', sent to the other side' : ''}`;
    case 'texting.number_p2p_removed':
      return `Stitch staff turned off person-to-person texting (P2P) for ${tel(d.number)}`;
    case 'texts.opted_out':
      return `${tel(d.number)} texted ${str(d.keyword) || 'STOP'} to ${tel(d.via)}, and gets no more texts from your numbers`;
    case 'texts.opted_in':
      return `${tel(d.number)} texted ${str(d.keyword) || 'START'} to ${tel(d.via)}, and gets texts from your numbers again`;
    case 'texts.taken_over':
      return `Took over a shared text conversation from ${str(d.fromName) || 'someone'}${str(d.team) ? ` (team ${str(d.team)})` : ''}`;
    case 'texts.handed':
      return `Handed a shared text conversation to ${str(d.toName) || 'someone'}${str(d.team) ? ` (team ${str(d.team)})` : ''}`;
    case 'texting.sent_automatic':
      return `Sent an automatic text to ${tel(d.to)} from ${tel(d.from)}`;
    case 'texting.sent':
      return `Sent a text to ${tel(d.to)} from ${tel(d.from)}`;
    case 'legal_hold.placed': {
      // 2717 widened holds: a number, a ring group or queue, one item, and a date range.
      const scope = str(d.scope);
      const on =
        scope === 'company'
          ? 'everything'
          : scope === 'person'
            ? 'a person'
            : scope === 'number'
              ? tel(d.number)
              : scope === 'queue'
                ? `ring group or queue ${str(d.extension)}`
                : scope === 'item'
                  ? str(d.item) === 'call'
                    ? 'a call'
                    : str(d.item) === 'recording'
                      ? 'a call recording'
                      : 'a voicemail'
                  : 'a conversation';
      const days = d.from && d.to ? `, ${billingDay(d.from)} to ${billingDay(d.to)}` : d.from ? `, from ${billingDay(d.from)}` : d.to ? `, up to ${billingDay(d.to)}` : '';
      return `Placed a legal hold on ${on}${days}`;
    }
    case 'legal_hold.lifted':
      return 'Lifted a legal hold';
    case 'company.call_settings':
      return 'Changed the voicemail email and call transcript settings';
    case 'voicemail_emails.checked':
      return `Checked the voicemail emails of mailbox ${str(d.mailbox)} (Stitch support)`;
    case 'voicemail_emails.mailbox_choice':
      return d.send === false ? `Turned off voicemail emails for mailbox ${str(d.mailbox)}` : `Changed voicemail emails for mailbox ${str(d.mailbox)}`;
    // Stitch support moving the company to Stitch's voicemail emails (Stitch Admin), and back.
    case 'voicemail_emails.moved': {
      const moved = list(d.moved);
      const failed = list(d.failed);
      const missed = failed.length ? ` (${plural(failed.length, 'mailbox', 'mailboxes')} couldn’t be: ${failed.join(', ')})` : '';
      if (moved.length === 0) return d.switched ? 'Switched on Stitch’s voicemail emails' + missed : `Moved voicemail emails to Stitch: nothing more to move${missed}`;
      const which = str(d.scope) === 'mailbox' && moved.length === 1 ? `mailbox ${moved[0]}` : plural(moved.length, 'mailbox', 'mailboxes');
      return `Moved voicemail emails to Stitch: the phone system’s own switched off for ${which}${d.switched ? ', and Stitch’s switched on' : ''}${missed}`;
    }
    case 'voicemail_emails.put_back': {
      const back = list(d.putBack);
      const failed = list(d.failed);
      const missed = failed.length ? ` (${plural(failed.length, 'mailbox', 'mailboxes')} couldn’t be: ${failed.join(', ')})` : '';
      const which = str(d.scope) === 'mailbox' && back.length === 1 ? `mailbox ${back[0]}` : plural(back.length, 'mailbox', 'mailboxes');
      return `Put the phone system’s own voicemail emails back for ${which}${d.switched && d.stitchEmails === 'off' ? ', and switched Stitch’s off' : ''}${missed}`;
    }
    case 'voicemail.greeting': {
      // 1.12.33: never the words themselves, only which greeting plays.
      const plays = str(d.plays) === 'typed' ? 'a typed greeting' : str(d.plays) === 'recorded' ? 'a recorded greeting' : 'the standard greeting';
      const who = str(d.extension) ? ` for extension ${str(d.extension)}` : '';
      return `${d.typed ? 'Typed a voicemail greeting' : 'Changed the voicemail greeting'}${who}: callers hear ${plays}${str(d.status) && str(d.status) !== 'applied' ? ` (it didn’t go in: ${str(d.status).replace(/_/g, ' ')})` : ''}`;
    }
    case 'wallboard.display_added':
      return `Set up a wallboard TV, “${str(d.name)}”`;
    case 'wallboard.display_changed':
      return `Changed a wallboard TV’s settings, “${str(d.name)}”`;
    case 'wallboard.display_removed':
      return `Turned off a wallboard TV, “${str(d.name)}”`;
    case 'change.applying':
      return `Applying: ${str(d.title)}`;
    // Someone's own phone, changed by an admin (or Stitch support) for them.
    case 'reception.seat_changed':
      if (d.inOperators !== undefined && !d.changeSet && str(d.seat) === 'receptionist') return `${d.inOperators ? 'Put' : 'Left'} extension ${str(d.extension)} ${d.inOperators ? 'in' : 'out of'} the Operators group`;
      return `${str(d.seat) === 'receptionist' ? 'Made extension' : 'Changed extension'} ${str(d.extension) || 'someone'} ${str(d.seat) === 'receptionist' ? 'a receptionist' : 'back to a standard seat'}${d.changeSet ? ' (a change to check and apply)' : ''}`;
    case 'reception.settings_changed':
      return 'Changed the reception settings';
    case 'reception.queue_backup':
      return `${d.on ? `Had the receptionists back up queue ${str(d.queue)} after ${str(d.seconds)} seconds` : `Turned off the receptionists’ backup for queue ${str(d.queue)}`}${d.changeSet ? ' (a change to check and apply)' : ''}`;
    case 'reception.menu_zero':
      return `${d.on ? `Had 0 in auto attendant ${str(d.menu)} ring the receptionists` : `Stopped 0 in auto attendant ${str(d.menu)} ringing the receptionists`}${d.changeSet ? ' (a change to check and apply)' : ''}`;
    // Saved copies of ring groups, queues and auto attendants (2718).
    case 'saved_copy.saved':
      return `Saved a copy of ${copyName(d)}${d.beforeRemoval ? ' before removing it' : ''}`;
    case 'saved_copy.rebuild_started':
      return `Started rebuilding ${copyName(d)} from a saved copy (it’s added once the change is applied)`;
    case 'saved_copy.downloaded':
      return `Downloaded the saved copy of ${copyName(d)}`;
    case 'saved_copy.removed':
      return `Removed the saved copy of ${copyName(d)}`;
    case 'dnd.turned_on':
    case 'dnd.turned_off':
      return `Turned do not disturb ${e.action === 'dnd.turned_on' ? 'on' : 'off'}${str(d.extension) ? ` for extension ${str(d.extension)}` : ''}`;
    case 'call.recording_transcribe':
      return 'Asked for a transcript of a call recording';
    case 'picture.changed':
      return `Changed ${str(d.user) ? `${str(d.user)}’s` : 'their'} picture`;
    case 'picture.removed':
      return `Removed ${str(d.user) ? `${str(d.user)}’s` : 'their'} picture`;
    // A file in a private channel is never named (admins who aren't in it read this).
    case 'messages.file_virus':
      return `Removed ${d.private ? 'a file in a private channel' : str(d.file) ? `the file ${q(d.file)}` : 'a file'} from Messages: it had a virus${str(d.virus) ? ` (${str(d.virus)})` : ''}`;
    // Scheduled changes (1.12.15): who scheduled or cancelled one, and how it went when it ran.
    case 'changes.scheduled': {
      const at = when(d.at);
      return str(d.action) === 'undo' ? `Scheduled putting it back${at ? ` on ${at}` : ''}: ${str(d.title)}` : `Scheduled${at ? ` for ${at}` : ''}: ${str(d.title)}`;
    }
    case 'changes.scheduled_cancelled':
      return `Cancelled a scheduled change: ${str(d.title)}`;
    case 'leland.scheduled_done':
      return `Done as scheduled: ${str(d.title)}`;
    case 'leland.scheduled_failed':
      return `Not done as scheduled: ${str(d.title)}`;
    // Numbers and 911 (Numbers, Get numbers, Move numbers to Stitch, Emergency addresses).
    case 'numbers.ordered': {
      const n = list(d.numbers).length;
      return n ? `Got ${plural(n, 'new number')}: ${tels(d.numbers)}` : 'Got new numbers';
    }
    case 'numbers.retried':
      return `Tried an order for new numbers again: ${ORDER_NOW[str(d.status)] ?? 'done'}`;
    case 'numbers.connected':
      return `Stitch staff connected ${tel(d.number)} to the phone system`;
    case 'numbers.port_requested': {
      const n = list(d.numbers).length;
      return n ? `Asked to move ${plural(n, 'number')} to Stitch: ${tels(d.numbers)}` : 'Asked to move numbers to Stitch';
    }
    case 'numbers.port_cancelled':
      return 'Cancelled a request to move numbers to Stitch';
    case 'numbers.port_completed': {
      const n = list(d.numbers).length;
      return n ? `${n === 1 ? 'A number' : `${n} numbers`} moved to Stitch: ${tels(d.numbers)}` : 'Numbers moved to Stitch';
    }
    case 'numbers.released':
      return `Gave up ${tel(d.number)}: it isn’t one of the company’s numbers any more`;
    case 'numbers.policy':
      if (d.canOrder === false) return 'Stopped the company’s admins getting new numbers themselves';
      return typeof d.monthlyLimit === 'number' ? `Let the company’s admins get up to ${plural(d.monthlyLimit, 'new number')} a month` : 'Changed who can get new numbers';
    case 'numbers.inventory_press_one':
      return d.on ? 'Open inventory now offers the main menu: press 1' : 'Open inventory stopped offering the main menu';
    case 'numbers.caller_name':
      return `Set the caller name for ${tel(d.number)}${str(d.name) ? ` to ${q(d.name)}` : ''}`;
    case 'numbers.call_flow':
      return `Pointed ${tel(d.number)} at the call flow for new numbers`;
    case 'e911.changed':
      return `Set the 911 address for ${tel(d.number)}${str(d.to) ? ` to ${str(d.to)}` : ''}${d.kept === false ? ' (it wasn’t kept as asked: check it on Emergency addresses)' : ''}`;
    case 'e911.removed':
      return `Took the 911 address off ${tel(d.number)}`;
    case 'e911.address_saved':
      return str(d.label) ? `Saved the 911 address ${q(d.label)}` : 'Saved a 911 address';
    // App calling (1.12.33): Stitch sets the extension's password itself and never shows it.
    case 'calling.sip_password_set':
      return str(d.why) === 'replaced'
        ? `Set a new App calling password for ${str(d.extension) ? `extension ${str(d.extension)}` : 'an extension'} (the phone system refused the old one)`
        : `Set up App calling for ${str(d.extension) ? `extension ${str(d.extension)}` : 'an extension'}`;
    // Live help: who allowed it and how it ended; never what was on screen.
    case 'live.allowed':
      return `Let ${str(d.staff) ? `${str(d.staff)} from Stitch support` : 'Stitch support'} see their screen for live help`;
    case 'live.declined':
      return `Said no to live help from ${str(d.staff) ? `${str(d.staff)} (Stitch support)` : 'Stitch support'}`;
    case 'live.control_allowed':
      return `Let ${str(d.staff) ? `${str(d.staff)} from Stitch support` : 'Stitch support'} click and type for them in live help`;
    case 'live.control_ended':
      return str(d.by) === 'staff' ? `Handed control back to ${str(d.user) || 'them'} in live help` : 'Took back control from Stitch support in live help';
    case 'live.ended': {
      const why = LIVE_ENDED[str(d.reason)];
      const secs = Number(d.seconds) || 0;
      const long = secs >= 60 ? `after ${plural(Math.round(secs / 60), 'minute')}` : secs > 0 ? 'after under a minute' : '';
      const how = [why, long].filter(Boolean).join(', ');
      return `Live help${str(d.user) ? ` with ${str(d.user)}` : ''} ended${how ? ` (${how})` : ''}`;
    }
    // Stitch staff, for the company (Stitch Admin).
    case 'customer.created':
      return `Stitch staff added ${str(d.customer) ? q(d.customer) : 'the company'} as a Stitch customer`;
    case 'customer.changed': {
      const fields = list(d.fields).map((f) => CUSTOMER_FIELDS[f] ?? f);
      return `Stitch staff changed the company’s details${fields.length ? `: ${fields.join(', ')}` : ''}`;
    }
    case 'customer.imported':
      return `Brought ${plural(Number(d.tenants) || 0, 'company', 'companies')} from the phone system into Stitch`;
    // Archive only (1.13.0, 2717): the company's service ended; History, Storage and Billing stay.
    case 'customer.archive_only':
      return 'Stitch staff made the company archive only: Call history, Storage and retention, legal holds, Activity and Billing stay; everything else is off';
    case 'customer.archive_only_ended':
      return `Stitch staff brought the company back from archive only${str(d.status) === 'onboarding' ? ' (setting up)' : ''}: everything is on again`;
    case 'customer.texting_registered':
      return 'Stitch staff let the company text anyway (registered outside Stitch): texts can go out from its numbers';
    case 'customer.texting_unregistered':
      return 'Stitch staff stopped letting the company text anyway: each number follows its texting registration';
    case 'tenant.created':
      return `Stitch staff set up the company on the phone system${d.partial ? ' (its name there still needs setting)' : ''}`;
    // Updates (1.12.33): what it did, never its result text (that's for Stitch staff).
    case 'updates.applied':
    case 'updates.put_back': {
      const u = updateOf(d.update);
      const [did, tried] = e.action === 'updates.applied' ? u.apply : u.putBack;
      return d.ok === false ? `Stitch staff tried to ${tried}, and it didn’t finish` : `Stitch staff ${did}`;
    }
    case 'updates.left_out':
      return `Stitch staff left the company out of ${updateOf(d.update).name}`;
    case 'updates.put_in':
      return `Stitch staff included the company in ${updateOf(d.update).name} again`;
    // Stitch staff's own settings: no company, so these show only in Stitch Admin. Never a value.
    case 'updates.bring_up_to_date':
      return `Brought every customer up to date (${plural(Number(d.customers) || 0, 'customer')})`;
    case 'updates.mailbox_policy_standard':
      return 'Changed Stitch’s standard for old voicemail';
    case 'voicemail_migration.move':
      return `Moved voicemail emails to Stitch for ${plural(Number(d.companies) || 0, 'company', 'companies')}`;
    case 'voicemail_migration.put_back':
      return `Put back the phone system’s own voicemail emails for ${plural(Number(d.companies) || 0, 'company', 'companies')}`;
    case 'staff.first_account':
      return 'Made the first Stitch staff account';
    case 'staff.disabled':
      return `Turned off Stitch staff access for ${str(d.user) || 'someone'}`;
    case 'staff.enabled':
      return `Turned on Stitch staff access for ${str(d.user) || 'someone'}`;
    case 'email.test_sent':
      return 'Sent a test email';
    case 'email.test_failed':
      return 'Tried to send a test email, and it didn’t go';
    case 'settings.carrier':
      return d.writes === true ? 'Changed the carrier settings and turned spending on' : d.writes === false ? 'Changed the carrier settings and turned spending off' : 'Changed the carrier settings';
    case 'settings.carrier.account_added':
      return `Added the carrier account ${q(d.name)}`.trim();
    case 'settings.carrier.account_changed':
      return d.default ? 'Made a carrier account the default' : 'Changed a carrier account';
    case 'settings.carrier.account_removed':
      return `Removed the carrier account ${q(d.name)}`.trim();
    case 'settings.carrier.moved':
      return 'Moved the carrier settings from the server’s variables into Stitch';
    case 'settings.gateway':
      return Array.isArray(d.removed) && d.removed.length ? 'Removed the call gateway settings saved in Stitch' : 'Changed the call gateway settings';
    case 'settings.gateway.moved':
      return 'Moved the call gateway settings from the server’s variables into Stitch';
    case 'settings.livekit':
      return Array.isArray(d.removed) && d.removed.length ? 'Removed the LiveKit settings saved in Stitch' : 'Changed the LiveKit settings';
    case 'settings.livekit.moved':
      return 'Moved the LiveKit settings from the server’s variables into Stitch';
    case 'settings.mail':
      return Array.isArray(d.removed) && d.removed.length ? 'Removed the email settings saved in Stitch' : 'Changed how Stitch sends its emails';
    case 'settings.mail.moved':
      return 'Moved the email settings from the server’s variables into Stitch';
    case 'settings.block_storage':
      return Array.isArray(d.removed) && d.removed.length ? 'Removed the Block Storage settings saved in Stitch' : 'Changed Stitch Block Storage’s key or bucket';
    case 'settings.block_storage.moved':
      return 'Moved the Block Storage settings from the server’s variables into Stitch';
    case 'settings.ai':
      return Array.isArray(d.removed) && d.removed.length ? 'Removed the AI settings saved in Stitch' : 'Changed the Claude API key or models';
    case 'settings.ai.moved':
      return 'Moved the AI settings from the server’s variables into Stitch';
    case 'settings.transcripts':
      return Array.isArray(d.removed) && d.removed.length ? 'Removed the transcription settings saved in Stitch' : 'Changed the transcription service';
    case 'settings.transcripts.moved':
      return 'Moved the transcription settings from the server’s variables into Stitch';
    case 'settings.recordings':
      return d.removed ? 'Removed the storage Stitch keeps recordings in' : 'Changed the storage Stitch keeps recordings in';
    case 'settings.transcription':
      return 'Changed the night catch-up hours for call transcripts';
    case 'settings.virus_check':
      return 'Changed the virus check’s address';
    case 'settings.webhooks':
      return 'Changed the webhooks address';
    case 'cdr_sender.added':
      return `Let ${str(d.address) || 'an address'} send call records`;
    case 'cdr_sender.note':
      return `Changed the note on call record sender ${str(d.address)}`.trim();
    case 'cdr_sender.removed':
      return `Stopped ${str(d.address) || 'an address'} sending call records`;
    // Billing (Stitch Admin → Billing, 1.13.0): Stitch staff only; the company's own ones show on its Activity.
    case 'billing.catalog_v1':
      return Number(d.added) > 0 ? `Loaded plan catalog v1 into billing (${plural(Number(d.added), 'price')})` : 'Loaded plan catalog v1 into billing (it was there already)';
    case 'billing.price_version':
      return `Set a new price for ${str(d.itemName) || str(d.code) || 'an item'}: ${dollars(d.monthlyCents)} a month from ${billingDay(d.effectiveFrom)}`;
    case 'billing.fee':
      return `Set the fee ${q(d.name) || 'on invoices'}: ${howMuch(d.method, d.amount)} ${FEE_BASIS[str(d.basis)] ?? ''}`.trim();
    // Teampace, Ceexo's system of record (3089): the company's Teampace client.
    case 'billing.teampace_linked':
      return `${d.how === 'created' ? 'Made the company’s Teampace client' : 'Linked the company to its Teampace client'}${str(d.name) ? ` ${q(d.name)}` : ''}${str(d.clientId) ? ` (#${str(d.clientId)})` : ''}`;
    case 'billing.teampace_updated':
      return `Sent the company’s changes to Teampace${list(d.fields).length ? `: ${list(d.fields).join(', ')}` : ''}`;
    case 'billing.teampace_failed':
      return `Couldn’t bring the company’s Teampace client in step${str(d.error) ? ` (${str(d.error)})` : ''}; Stitch tries again`;
    case 'billing.teampace_review':
      return `Found ${str(d.candidates) || 'several'} Teampace clients that could be the company’s; Stitch staff choose`;
    case 'billing.teampace_chosen':
      return d.clientId ? `Stitch staff linked the company to Teampace client #${str(d.clientId)}` : 'Stitch staff asked for a new Teampace client for the company';
    case 'billing.teampace_invoice':
      return `Sent invoice ${str(d.number)} to Teampace${typeof d.totalCents === 'number' ? ` (${dollars(d.totalCents)})` : ''}, with its PDF`;
    case 'billing.teampace_credit_note':
      return `Sent credit note ${str(d.number)}${str(d.invoice) ? ` for ${str(d.invoice)}` : ''} to Teampace`;
    case 'billing.teampace_voided':
      return `Cancelled invoice ${str(d.number)} in Teampace (it was voided in Stitch)`;
    case 'billing.teampace_send_failed':
      return `Couldn’t send ${str(d.key).startsWith('credit:') ? 'a credit note' : str(d.key).startsWith('void:') ? 'a void' : 'an invoice'} to Teampace${str(d.error) ? ` (${str(d.error)})` : ''}; Stitch tries again`;
    case 'billing.teampace_push':
      return d.on ? 'Stitch staff turned on sending issued invoices to Teampace' : 'Stitch staff turned off sending issued invoices to Teampace';
    case 'billing.payment_method_page':
      return 'Opened the page for adding a card or bank account';
    case 'billing.payment_method_default':
      return `Made ${str(d.card) || 'a card'} the default for paying invoices`;
    case 'billing.payment_method_removed':
      return `Removed ${str(d.card) || 'a card'} from the company’s payment methods`;
    case 'billing.autopay':
      return d.on ? 'Turned on autopay (invoices paid with the default card)' : 'Turned off autopay';
    case 'billing.card_expiring':
      return `Reminded the billing contacts that ${str(d.card) || 'a card'} expires ${str(d.expires)}`;
    // Payments through Teampace's card plugin (3091).
    case 'billing.teampace_payments':
      return d.on ? 'Stitch staff turned on taking payments (autopay and Pay now)' : 'Stitch staff turned off taking payments';
    case 'billing.pay_now':
      return `Paid invoice ${str(d.number)}${typeof d.cents === 'number' ? ` (${dollars(d.cents)})` : ''}${str(d.card) ? ` with ${str(d.card)}` : ''} (Pay now)`;
    case 'billing.pay_page':
      return `Opened the payment page for invoice ${str(d.number) || ''}`.trim();
    case 'billing.payment_succeeded':
      return `Payment received for invoice ${str(d.number)}${typeof d.cents === 'number' ? `: ${dollars(d.cents)}` : ''}${str(d.card) ? ` from ${str(d.card)}` : ''}${str(d.how) ? ` (${str(d.how)})` : ''}`;
    case 'billing.payment_failed':
      return `A payment for invoice ${str(d.number)} didn’t go through${str(d.card) ? ` (${str(d.card)})` : ''}${str(d.reason) ? `: ${str(d.reason)}` : ''}${str(d.how) ? ` (${str(d.how)})` : ''}`;
    case 'billing.payment_refunded':
      return `Refunded ${typeof d.cents === 'number' ? dollars(d.cents) : 'a payment'} on invoice ${str(d.number)}${str(d.reason) ? ` (${str(d.reason)})` : ''}`;
    case 'billing.charge_error':
      return `Invoice ${str(d.number)} wasn’t charged${str(d.error) ? `: ${str(d.error)}` : ''}`;
    case 'billing.dunning': {
      const n = str(d.number);
      const step: Record<string, string> = {
        reminded: `Reminded the billing contacts that invoice ${n} is past due`,
        blocked: `Invoice ${n} is unpaid after its retries: new people and numbers are blocked until it’s paid (first written notice sent)`,
        warned: `Sent the final written notice for unpaid invoice ${n}`,
        suspended: `Suspended the billing account: invoice ${n} unpaid at 30 days (calls and 911 go on)`,
        resolved: d.lifted ? `Invoice ${n} is paid: blocks and the suspension are lifted` : `Invoice ${n} is ${d.voided ? 'void' : 'paid'}: nothing is blocked for it any more`
      };
      return step[str(d.step)] ?? `Invoice ${n}: a step for an unpaid invoice`;
    }
    // The nightly reconciliation with Teampace (3093): Stitch staff only.
    case 'billing.teampace_reconciled':
      return `Compared Stitch with Teampace: ${str(d.invoices) || '0'} invoices, ${str(d.open) || '0'} ${Number(d.open) === 1 ? 'difference' : 'differences'} open${Number(d.new) ? ` (${str(d.new)} new)` : ''}`;
    case 'billing.teampace_month_signed':
      return `Signed off ${str(d.month)} between Stitch and Teampace${d.matches ? '' : ' (with differences)'}`;
    case 'billing.teampace_started':
      return 'Stitch staff turned on syncing new companies to Teampace';
    case 'billing.teampace_review_applied':
      return `Stitch staff linked current companies to Teampace: ${str(d.linked) || '0'} linked, ${str(d.created) || '0'} made, ${str(d.skipped) || '0'} left for later`;
    case 'billing.account': {
      if (d.opened) return `Stitch staff set up the company’s billing account${str(d.number) ? ` (${str(d.number)})` : ''}${str(d.billsFrom) ? `, billing from ${billingDay(d.billsFrom)}` : ''}`;
      const fields = Object.keys(ACCOUNT_FIELDS).filter((k) => d[k] !== undefined && d[k] !== null);
      return `Stitch staff changed the company’s billing account${fields.length ? `: ${fields.map((k) => ACCOUNT_FIELDS[k]).join(', ')}` : ''}`;
    }
    case 'billing.cancel':
      return `Stitch staff cancelled the company’s service: it runs through ${billingDay(d.cancelAt)}, then a final invoice`;
    case 'billing.sync': {
      const parts = [Number(d.added) ? `${Number(d.added)} added` : '', Number(d.removed) ? `${Number(d.removed)} removed` : '', Number(d.changed) ? `${Number(d.changed)} changed` : ''].filter(Boolean);
      return `Brought billing in step with the company’s people${parts.length ? ` (${parts.join(', ')})` : ' (nothing to change)'}`;
    }
    case 'billing.event': {
      const item = str(d.itemName) || str(d.item) || 'an item';
      const who = billingWho(d);
      if (str(d.change) === 'remove') return `Stitch staff removed ${item} for ${who} (billed to the end of the month)`;
      if (str(d.change) === 'change') return `Stitch staff moved ${who} to ${item}`;
      return `Stitch staff added ${item} for ${who}`;
    }
    case 'billing.discount':
      return `Stitch staff added a discount${q(d.label) ? ` ${q(d.label)}` : ''}: ${howMuch(d.method, d.value)} off ${str(d.scope) === 'item' ? str(d.itemName) || 'one item' : 'the invoice'}${d.waiting ? ' (waiting for a second person at Stitch to approve it)' : ''}`;
    case 'billing.discount_approved':
      return `Stitch staff approved the discount${q(d.label) ? ` ${q(d.label)}` : ''}`;
    case 'billing.promo_code':
      return `Made the promo code ${str(d.code) || 'for sign-ups'}: ${howMuch(d.method, d.value)} off`;
    case 'billing.promo_used':
      return `Used the promo code ${str(d.code) || 'for a discount'}`;
    case 'billing.adjustment':
      return `Stitch staff put a one-off ${str(d.kind) === 'credit' ? 'credit' : str(d.kind) === 'fee' ? 'fee' : 'charge'} on the next invoice: ${str(d.description) || 'no description'}, ${dollars(str(d.kind) === 'credit' ? -Number(d.cents) : d.cents)}`;
    case 'billing.credit':
      return `Stitch staff added ${dollars(d.cents)} of account credit${str(d.reason) ? `: ${str(d.reason)}` : ''}`;
    case 'billing.first_invoice':
      return `Issued the first invoice${str(d.invoice) ? `, ${str(d.invoice)}` : ''}: ${dollars(d.totalCents)}`;
    case 'billing.preview':
      return `Made ${billingMonth(d.period)}’s invoices as drafts to check (${plural(Number(d.invoices) || 0, 'invoice')})`;
    case 'billing.issue_run':
      return `Issued ${billingMonth(d.period)}’s invoices: ${Number(d.issued) || 0} issued${Number(d.held) ? `, ${Number(d.held)} held` : ''}${Number(d.already) ? `, ${Number(d.already)} issued before` : ''}${Number(d.waiting) ? `, ${Number(d.waiting)} waiting for usage to be counted` : ''}`;
    case 'billing.hold':
      return `Stitch staff held the company’s ${billingMonth(d.period)} invoice to check it before it goes out`;
    case 'billing.release':
      return `Stitch staff let the company’s ${billingMonth(d.period)} invoice go out with the others`;
    case 'billing.void':
      return `Stitch staff voided invoice ${str(d.invoice) || ''}${str(d.reason) ? `: ${str(d.reason)}` : ''}`.replace(' :', ':');
    // Usage pricing (stitch-rating, 3097).
    case 'billing.rating_address':
      return str(d.serviceUrl) ? `Set where the rating service is: ${str(d.serviceUrl)}` : 'Let Stitch find the rating service by itself';
    case 'billing.usage_price': {
      const name = USAGE_METERS[str(d.meter)] ?? (str(d.meter) || 'usage');
      const launch = d.launchUnitCents === null || d.launchUnitCents === undefined ? '' : `, launch price ${unitCents(d.launchUnitCents)}`;
      return `Set a new usage price for ${name}: ${unitCents(d.listUnitCents)} a unit${launch}, from ${billingDay(d.effectiveFrom)}`;
    }
    case 'billing.usage_repriced':
      return `Priced ${billingMonth(d.month)}’s ${USAGE_METERS[str(d.meter)] ?? 'usage'} again at today’s prices (${plural(Number(d.changed) || 0, 'event')} changed)${str(d.reason) ? `: ${str(d.reason)}` : ''}`;
    // Calls abroad and to premium-rate numbers (3186).
    case 'billing.calls_paused':
      return str(d.why) === 'off'
        ? 'Paused calls abroad and to premium-rate numbers: they aren’t turned on for the company'
        : `Paused calls abroad and to premium-rate numbers: ${str(d.why) === 'daily' ? `today’s came to ${dollars(d.todayCents)}, over the daily limit of ${dollars(d.dailyCents)}` : `this month’s came to ${dollars(d.monthCents)}, over the monthly limit of ${dollars(d.monthlyCents)}`}`;
    case 'billing.calls_resumed':
      return 'Started calls abroad and to premium-rate numbers again';
    case 'billing.call_limits': {
      const parts: string[] = [];
      if (d.international === true) parts.push('turned on calls abroad and to premium-rate numbers');
      if (d.international === false) parts.push('turned off calls abroad and to premium-rate numbers');
      if (d.dailyCents !== undefined) parts.push(`set the daily limit to ${dollars(d.dailyCents)} (was ${dollars(d.wasDailyCents)})`);
      if (d.monthlyCents !== undefined) parts.push(`set the monthly limit to ${dollars(d.monthlyCents)} (was ${dollars(d.wasMonthlyCents)})`);
      return parts.length ? `Stitch staff ${parts.join(', ')}` : 'Stitch staff saved the limits for calls abroad (nothing changed)';
    }
    case 'billing.calls_let_through':
      return `Stitch staff let calls abroad through past the limits until ${when(d.until)}${str(d.reason) ? `: ${str(d.reason)}` : ''}`;
    case 'billing.calls_let_through_ended':
      return 'Stitch staff ended letting calls abroad through past the limits';
    case 'billing.pause_plan':
      return str(d.plan) ? `Chose the dial plan that pauses calls abroad: ${str(d.plan)}` : 'Cleared the dial plan that pauses calls abroad';
    case 'billing.rate_table':
      return `Added rate table ${Number(d.version) || ''} for calls abroad: ${plural(Number(d.rates) || 0, 'rate')} from ${billingDay(d.effectiveFrom)}${Number(d.markupPercent) ? `, ${Number(d.markupPercent)}% over cost` : ''}${Number(d.nowRated) ? ` (${plural(Number(d.nowRated), 'call')} with no rate now priced)` : ''}`;
    case 'billing.company_rate':
      return d.unitCents === null || d.unitCents === undefined
        ? `Removed the company’s own price for calls to +${str(d.prefix)}`
        : `Set the company’s own price for calls to +${str(d.prefix)}: ${unitCents(d.unitCents)} a minute`;
    case 'billing.credit_note':
      return `Stitch staff issued credit note ${str(d.number)}${str(d.invoice) ? ` on invoice ${str(d.invoice)}` : ''}: ${dollars(d.totalCents)}`;
    // Invoice files, emails and billing contacts (3099).
    case 'billing.invoice_emailed': {
      const sent = Array.isArray(d.sent) ? d.sent.filter((x): x is string => typeof x === 'string') : [];
      if (sent.length) return `Stitch staff emailed invoice ${str(d.invoice)} to ${sent.join(', ')}${Number(d.failed) ? ` (${plural(Number(d.failed), 'email')} couldn’t be sent)` : ''}`;
      if (Number(d.failed)) return `Stitch staff tried to email invoice ${str(d.invoice)}, but it couldn’t be sent`;
      return `Stitch staff tried to email invoice ${str(d.invoice)}${str(d.skipped) ? `: ${str(d.skipped)}` : ''}`;
    }
    case 'billing.invoice_email_bounced':
      return `Invoice ${str(d.invoice)} to ${str(d.to)} bounced${str(d.why) ? `: ${str(d.why)}` : ''}`;
    case 'billing.invoice_email_complained':
      return `Invoice ${str(d.invoice)} to ${str(d.to)} was marked as spam there`;
    case 'billing.settings': {
      const parts: string[] = [];
      if (typeof d.customersSeeBilling === 'boolean') parts.push(d.customersSeeBilling ? 'showed Billing to customers in their portal' : 'hid Billing from customers in their portal');
      if (typeof d.sendInvoiceEmails === 'boolean') parts.push(d.sendInvoiceEmails ? 'turned invoice emails to customers on' : 'turned invoice emails to customers off');
      if (typeof d.taxesOn === 'boolean') parts.push(d.taxesOn ? 'turned taxes from the tax table on' : 'turned taxes from the tax table off');
      if (typeof d.taxResearchOn === 'boolean') parts.push(d.taxResearchOn ? 'turned tax research by Claude on' : 'turned tax research by Claude off');
      if (d.issuerAddress !== undefined || d.issuerEmail !== undefined || d.issuerPhone !== undefined) parts.push('changed Ceexo’s details shown on invoices');
      return `Stitch staff ${parts.length ? parts.join(' and ') : 'changed the billing settings'}`;
    }
    case 'billing.contacts': {
      const list = Array.isArray(d.contacts) ? d.contacts.filter((x): x is string => typeof x === 'string') : [];
      return list.length ? `Set who gets the company’s invoices: ${list.join(', ')}` : 'Removed the company’s billing contacts (invoices go to its admins)';
    }
    // The tax table (3098).
    case 'billing.tax_rate_added':
      return `Stitch staff added a tax rate for review: ${str(d.name)} (${str(d.place)})`;
    case 'billing.tax_rate_approved':
      return `Stitch staff approved the tax rate ${str(d.name)} (${str(d.place)})`;
    case 'billing.tax_rate_retired':
      return d.removed ? `Stitch staff removed the draft tax rate ${str(d.name)} (${str(d.place)})` : `Stitch staff ended the tax rate ${str(d.name)} (${str(d.place)}) after ${billingDay(d.lastDay)}`;
    case 'billing.tax_research_run':
      return `Stitch staff ran the tax research now (${plural(Number(d.researched) || 0, 'place')} looked into, ${plural(Number(d.drafts) || 0, 'draft')} added)`;
    case 'billing.service_address':
      return `Stitch staff set the company’s service address for taxes (${str(d.city)}, ${str(d.state)})${d.matched === false ? ': it didn’t match a place yet' : ''}`;
    case 'billing.tax_exemption':
      return `Stitch staff added the company’s tax exemption certificate${Array.isArray(d.kinds) && d.kinds.length ? ` (${d.kinds.join(', ')})` : ''}`;
    case 'billing.tax_exemption_revoked':
      return 'Stitch staff ended the company’s tax exemption';
    case 'billing.bill_to':
      return `Changed the company’s billing address${str(d.city) ? ` (${str(d.city)}${str(d.state) ? `, ${str(d.state)}` : ''})` : ''}`;
    default:
      return activityFallback(e.action);
  }
}

/** Who did what in the portal, and (when the company records sessions) what it looked like. */
export function TenantActivity() {
  const { tenant } = useTenant();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'recordings' ? 'recordings' : 'all';
  const person = params.get('person') ?? '';
  const setFilter = (next: { tab?: string; person?: string }) => {
    const t = next.tab ?? tab;
    const p = next.person ?? person;
    setParams({ ...(t === 'recordings' ? { tab: t } : {}), ...(p ? { person: p } : {}) });
  };
  // Fresh each time the tab is shown (watching a recording adds a line).
  const data = useData(() => api.activity(tenant.domain, person ? { user: person } : {}), [tenant.domain, person, tab]);
  const people = data.data?.people ?? [];

  return (
    <>
      <PageHeader
        eyebrow={`Company · ${timesIn()}`}
        title="Activity"
        actions={
          <Button icon="refresh" onClick={data.reload}>
            Refresh
          </Button>
        }
      />
      <div className="content">
        <div className="activity-bar">
          <div className="filters" role="tablist" aria-label="Activity">
            <button type="button" role="tab" aria-selected={tab === 'all'} aria-pressed={tab === 'all'} onClick={() => setFilter({ tab: 'all' })}>
              What people did
            </button>
            <button type="button" role="tab" aria-selected={tab === 'recordings'} aria-pressed={tab === 'recordings'} onClick={() => setFilter({ tab: 'recordings' })}>
              Session recordings
            </button>
          </div>
          <label className="activity-person">
            <span className="sr-only">Person</span>
            <select className="input" value={person} onChange={(e) => setFilter({ person: e.target.value })}>
              <option value="">Everyone</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {tab === 'all' ? <Everything domain={tenant.domain} person={person} first={data} /> : <Recordings domain={tenant.domain} person={person} />}
      </div>
    </>
  );
}

function Everything({ domain, person, first }: { domain: string; person: string; first: DataState<Awaited<ReturnType<typeof api.activity>>> }) {
  // Earlier pages stay open when the first page loads again by itself.
  const earlier = useEarlier(first.data?.entries, first.data?.more, `${domain}|${person}`, (e) => e.at);
  const hasMore = earlier.more;
  const [loading, setLoading] = useState(false);
  const base = `/t/${encodeURIComponent(domain)}`;

  if (first.error)
    return (
      <Card>
        <LoadError error={first.error} retry={first.reload} />
      </Card>
    );
  if (!first.data)
    return (
      <Card>
        <Loading label="Loading activity" />
      </Card>
    );
  const entries = earlier.list;
  const loadMore = async () => {
    const last = entries[entries.length - 1];
    if (!last) return;
    setLoading(true);
    try {
      const r = await api.activity(domain, { ...(person ? { user: person } : {}), before: last.at });
      earlier.addEarlier(r.entries, r.more);
    } finally {
      setLoading(false);
    }
  };

  // One heading per day.
  let day = '';
  return (
    <Card>
      {entries.length === 0 ? (
        <Empty title="Nothing yet">{person ? 'This person hasn’t done anything in the portal yet.' : 'What people do in the portal shows up here.'}</Empty>
      ) : (
        <ol className="activity-list">
          {entries.map((e) => {
            const d = dateOnly(e.at);
            const heading = d !== day ? d : null;
            day = d;
            const cs = str(e.detail.changeSet);
            return (
              <li key={e.id} className={heading ? 'activity-day-start' : undefined}>
                {heading && <h3 className="activity-day">{heading}</h3>}
                <div className={`activity-row act-${e.action.split('.')[0]}`}>
                  <time className="num muted" dateTime={e.at} title={fullWhen(e.at)}>
                    {shortWhen(e.at)}
                  </time>
                  <span className="activity-who">{e.who}</span>
                  <span className="activity-what">
                    {describeActivity(e)}
                    {cs && (
                      <>
                        {' '}
                        <Link className="link-btn" to={`${base}/history?open=${encodeURIComponent(cs)}`}>
                          Details
                        </Link>
                      </>
                    )}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {hasMore && (
        <div className="card-foot center">
          <Button variant="secondary" disabled={loading} onClick={() => void loadMore()}>
            {loading ? 'Loading…' : 'Show earlier'}
          </Button>
        </div>
      )}
    </Card>
  );
}

function Recordings({ domain, person }: { domain: string; person: string }) {
  const data = useData(() => Promise.all([api.screenRecordings(domain, person || undefined), api.recordingSettings(domain)]), [domain, person]);
  // Earlier pages stay open when the first page loads again by itself.
  const earlier = useEarlier(data.data?.[0].recordings, data.data?.[0].more, `${domain}|${person}`, (r) => r.startedAt);
  const more = earlier.more;
  const [loadingMore, setLoadingMore] = useState(false);
  const [removing, setRemoving] = useState<SessionRecordingView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const base = `/t/${encodeURIComponent(domain)}`;

  if (data.error)
    return (
      <Card>
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  if (!data.data)
    return (
      <Card>
        <Loading label="Loading recordings" />
      </Card>
    );
  const [first, settings] = data.data;
  const recordings = earlier.list;
  const showEarlier = async () => {
    const last = recordings[recordings.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const r = await api.screenRecordings(domain, person || undefined, last.startedAt);
      earlier.addEarlier(r.recordings, Boolean(r.more));
    } finally {
      setLoadingMore(false);
    }
  };
  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setError('');
    try {
      await api.deleteScreenRecording(domain, removing.id);
      earlier.drop(removing.id);
      setRemoving(null);
      data.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!settings.enabled && (
        <Notice title="Session recording is off">
          Switch it on, choose who is recorded and how long recordings are kept, under <Link to={`${base}/settings`}>Settings</Link>.
        </Notice>
      )}
      <Card>
        {recordings.length === 0 ? (
          <Empty title="No recordings">{settings.enabled ? 'Recordings show up here a few seconds after someone in a recorded role signs in.' : 'Nothing has been recorded.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Started</th>
                  <th scope="col">Person</th>
                  <th scope="col" className="hide-sm">
                    Length
                  </th>
                  <th scope="col" className="hide-sm">
                    Pages
                  </th>
                  <th scope="col">Kept on</th>
                  <th scope="col" className="hide-sm">
                    Stitch deletes it
                  </th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((r) => {
                  const len = Date.parse(r.lastAt) - Date.parse(r.startedAt);
                  const onStitch = r.where.some((w) => w !== 'theirs');
                  return (
                    <tr key={r.id}>
                      <td className="num" title={fullWhen(r.startedAt)}>
                        {shortWhen(r.startedAt)}
                      </td>
                      <td>{r.userName}</td>
                      <td className="num hide-sm">{len < 60_000 ? 'under a minute' : plural(Math.round(len / 60_000), 'minute')}</td>
                      <td className="hide-sm">
                        <span className="muted" title={r.pages.map(pageName).join(', ')}>
                          {r.pages.length ? `${pageName(r.pages[0] ?? '')}${r.pages.length > 1 ? ` and ${r.pages.length - 1} more` : ''}` : '—'}
                        </span>
                      </td>
                      <td>
                        <span className="rec-where-chips">
                          {r.where.map((w) => (
                            <StorageChip key={w} place={w} {...(w === 'theirs' && r.bucket ? { bucket: r.bucket } : {})} />
                          ))}
                          {r.where.length === 0 && <span className="muted">Nowhere</span>}
                        </span>
                        {r.exportError && (
                          <small className="crit-text rec-export-error" title={r.exportError}>
                            Copy to your storage failed. Stitch keeps trying.
                          </small>
                        )}
                      </td>
                      <td className="num hide-sm">{onStitch ? dateOnly(r.expiresAt) : '—'}</td>
                      <td className="actions">
                        <a className="btn secondary small" href={replayUrl(domain, r.id)} target="_blank" rel="noopener">
                          <Icon name="play" size={15} />
                          Watch
                        </a>
                        {onStitch && (
                          <Button size="small" variant="ghost" icon="trash" aria-label={`Delete ${r.userName}’s recording`} onClick={() => setRemoving(r)}>
                            <span className="sr-only">Delete</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {more && (
          <div className="card-foot center">
            <Button variant="secondary" disabled={loadingMore} onClick={() => void showEarlier()}>
              {loadingMore ? 'Loading…' : 'Show earlier'}
            </Button>
          </div>
        )}
      </Card>
      <Dialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title="Delete this recording?"
        foot={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Keep it
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        <p>
          {removing?.userName}’s session from {removing ? fullWhen(removing.startedAt) : ''} is deleted from Stitch now. This can’t be undone.
          {removing?.where.includes('theirs') ? ' The copy in your own storage stays there.' : ''}
        </p>
        {error && <Notice tone="crit">{error}</Notice>}
      </Dialog>
    </>
  );
}
